"use client"
import { useLogger } from "next-axiom"
import { useEffect, useRef } from "react"

/**
 * JSON replacer that serializes Error and File values for client-side logging.
 *
 * @param {string} key
 * @param {unknown} value
 * @returns {unknown}
 */
function consoleErrorReplacer(key, value) {
  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: value.stack,
      cause: value.cause,
    }
  }

  if (typeof File !== "undefined" && value instanceof File) {
    return { name: value.name, size: value.size, type: value.type }
  }

  return value
}

/**
 * Formats a console.error argument for Axiom (objects are stringified, not coerced).
 *
 * @param {unknown} arg
 * @returns {string}
 */
function formatConsoleErrorArg(arg) {
  if (typeof arg === "string") {
    return arg
  }

  if (arg instanceof Error) {
    return arg.message
  }

  if (typeof arg === "object" && arg !== null) {
    try {
      return JSON.stringify(arg, consoleErrorReplacer)
    } catch {
      return String(arg)
    }
  }

  return String(arg)
}

/**
 * Finds the first Error in console.error arguments (including nested { error } objects).
 *
 * @param {unknown[]} args
 * @returns {Error | undefined}
 */
function findConsoleError(args) {
  for (const arg of args) {
    if (arg instanceof Error) {
      return arg
    }

    if (arg && typeof arg === "object" && "error" in arg && arg.error instanceof Error) {
      return arg.error
    }
  }

  return undefined
}

export function ClientErrorLogger({ data = {} }) {
  const log = useLogger().with(data)
  const logRef = useRef(log)
  const isPatched = useRef(false)

  // Keep the log reference up to date without triggering re-runs
  useEffect(() => {
    logRef.current = log
  }, [log])

  useEffect(() => {
    // Prevent multiple patches
    if (isPatched.current) {
      return
    }
    isPatched.current = true

    const userAgent = window.navigator.userAgent

    const getPageContext = () => ({
      host: window.location.hostname,
      url: window.location.href,
      path: window.location.pathname,
    })

    // Catches most uncaught exceptions
    window.onerror = function (message, source, lineno, colno, error) {
      logRef.current.error(message?.toString() || "Unhandled error", {
        ...getPageContext(),
        userAgent: userAgent,
        statusCode: 0,
        error: error?.name ?? "Error",
        cause: error?.cause,
        stack: error?.stack,
        kind: "window.onerror",
      })
    }

    // Catches unhandled Promise rejections
    window.onunhandledrejection = function (event) {
      const err = event.reason instanceof Error ? event.reason : new Error(event.reason)

      logRef.current.error(err.message || "Unhandled rejection", {
        ...getPageContext(),
        userAgent: userAgent,
        statusCode: 0,
        error: err.name,
        cause: err.cause,
        stack: err.stack,
        kind: "window.onunhandledrejection",
      })
    }

    // Catch console.error logs - store ORIGINAL before any patching
    const originalConsoleError = console.error

    console.error = function (...args) {
      originalConsoleError.apply(console, args)

      const error = new Error()
      const message = args.map(formatConsoleErrorArg).join(" ") || "console.error triggered"
      const topError = findConsoleError(args) || {}

      logRef.current.error(message || "Console.error", {
        ...getPageContext(),
        userAgent: userAgent,
        error: topError.name || "UnknownError",
        cause: topError.cause || undefined,
        stack: topError.stack || error.stack,
        kind: "console.error",
      })
    }

    // Cleanup: restore original console.error on unmount
    return () => {
      console.error = originalConsoleError
      isPatched.current = false
    }
  }, [])

  return null
}
